/**
 * quality/qualityControl.ts — validate, de-duplicate, and score incoming data.
 *
 * Raw provider output is never trusted. Before anything is cached or handed to
 * an engine it is: validated (incomplete records dropped), de-duplicated,
 * confidence-ranked (completeness × provider trust × freshness), and staleness-
 * flagged. Pure functions — no side effects.
 */

import type {
  DataConfidence,
  FusedEntity,
  FusionDomain,
  VerificationStatus,
} from '../models';

/** Required fields per domain family — records missing these are dropped. */
function isComplete(domain: FusionDomain, record: Record<string, unknown>): boolean {
  const has = (k: string) => record[k] != null && record[k] !== '';
  switch (domain) {
    case 'weather':
      return typeof record.tempC === 'number';
    case 'currency':
    case 'currencyExchange':
      return typeof record.rate === 'number' && has('quoteCurrency');
    case 'safety':
    case 'travelAdvisory':
      return typeof record.overallScore === 'number' || typeof record.advisoryLevel === 'number';
    case 'events':
      return has('title') && typeof record.startsAt === 'number';
    case 'communityReviews':
      return typeof record.averageRating === 'number';
    case 'housing':
    case 'hotel':
    case 'hostel':
    case 'monthlyRental':
      return has('title') && record.location != null;
    case 'internetSpeed':
    case 'airQuality':
    case 'costOfLiving':
    case 'visa':
      return typeof record.value === 'number';
    default:
      // Place-like domains need at least a name and a location.
      return has('name') && record.location != null;
  }
}

/** How many of a small set of "signal" fields are present (0–1). */
function completenessRatio(record: Record<string, unknown>): number {
  const signalKeys = [
    'name', 'title', 'rating', 'reviewCount', 'priceLevel', 'address',
    'monthlyCostUsd', 'internetMbps', 'distanceKm', 'summary', 'value',
  ];
  const present = signalKeys.filter((k) => record[k] != null).length;
  const applicable = signalKeys.filter((k) => k in record).length || 1;
  return present / applicable;
}

/** Validate + drop incomplete records. */
export function validateRecords<T extends FusedEntity>(domain: FusionDomain, records: T[]): T[] {
  return records.filter((r) => isComplete(domain, r as unknown as Record<string, unknown>));
}

/** Remove duplicates by a domain-aware identity key. */
export function deduplicateRecords<T extends FusedEntity>(records: T[]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const r of records) {
    const rec = r as unknown as Record<string, unknown>;
    const name = String(rec.name ?? rec.title ?? r.id).trim().toLowerCase();
    const loc = rec.location as { latitude?: number; longitude?: number } | undefined;
    const geo = loc ? `${loc.latitude?.toFixed(3)},${loc.longitude?.toFixed(3)}` : '';
    const key = `${name}|${geo}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(r);
  }
  return out;
}

export interface ConfidenceInputs {
  providerId: string;
  /** Provider's base trust 0–100. */
  baseConfidence: number;
  lastUpdated: number;
  now: number;
  ttlMs: number;
}

/** Compute a data-confidence envelope: completeness × trust × freshness. */
export function scoreConfidence(
  record: Record<string, unknown>,
  inputs: ConfidenceInputs,
): DataConfidence {
  const completeness = completenessRatio(record);
  const ageMs = Math.max(0, inputs.now - inputs.lastUpdated);
  const freshness = inputs.ttlMs > 0 ? Math.max(0, 1 - ageMs / inputs.ttlMs) : 1;
  const stale = ageMs > inputs.ttlMs;

  const confidence = Math.round(
    inputs.baseConfidence * (0.5 + 0.3 * completeness + 0.2 * freshness),
  );

  let verificationStatus: VerificationStatus;
  if (stale) verificationStatus = 'stale';
  else if (inputs.baseConfidence >= 85 && completeness >= 0.6) verificationStatus = 'verified';
  else if (completeness < 0.4) verificationStatus = 'estimated';
  else verificationStatus = 'unverified';

  return {
    confidence: Math.max(0, Math.min(100, confidence)),
    lastUpdated: inputs.lastUpdated,
    providerSource: inputs.providerId,
    verificationStatus,
  };
}

/** Stamp confidence onto every record and sort best-first. */
export function rankByConfidence<T extends FusedEntity>(
  records: T[],
  inputs: ConfidenceInputs,
): T[] {
  return records
    .map((r) => ({
      ...r,
      meta: scoreConfidence(r as unknown as Record<string, unknown>, inputs),
    }))
    .sort((a, b) => b.meta.confidence - a.meta.confidence);
}

/** Aggregate confidence for a result set (average, 0 for empty). */
export function aggregateConfidence(records: FusedEntity[]): number {
  if (records.length === 0) return 0;
  return Math.round(records.reduce((sum, r) => sum + r.meta.confidence, 0) / records.length);
}

/**
 * Re-age a record's confidence envelope at READ time. Confidence decays with
 * age and `verificationStatus` flips to `stale` once the record is older than
 * its domain TTL. Used when serving cached / stale-cache results so the envelope
 * always reflects the data's real age — never the age it had when first fetched.
 */
export function applyFreshnessDecay(meta: DataConfidence, now: number, ttlMs: number): DataConfidence {
  const ageMs = Math.max(0, now - meta.lastUpdated);
  const freshness = ttlMs > 0 ? Math.max(0, Math.min(1, 1 - ageMs / ttlMs)) : 1;
  const stale = ttlMs > 0 && ageMs > ttlMs;
  const confidence = Math.max(0, Math.min(100, Math.round(meta.confidence * (0.6 + 0.4 * freshness))));
  return {
    ...meta,
    confidence,
    verificationStatus: stale ? 'stale' : meta.verificationStatus,
  };
}

/** Re-age every record in a set (returns new records; inputs untouched). */
export function reageRecords<T extends FusedEntity>(records: T[], now: number, ttlMs: number): T[] {
  return records.map((r) => ({ ...r, meta: applyFreshnessDecay(r.meta, now, ttlMs) }));
}
